import { HttpStatusCode, isAxiosError } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import type {
  KaokuNumberToChibanSearchParam,
  CreateKaokuChibanSearchRequestResponse,
  SearchResultRow,
  KaokuNumberInputRow,
  KaokuNumberInput,
  ChibanSearchRequest,
} from "@/types/kaokuNumber";
import { type PropertySelectionRow } from "@/features/map/types";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { type BookTypeEng } from "@/types/acquirebook";
import { useState } from "react";
import { SendToTrackingService } from "@/api/handlers/errors";
import { NYFillWidthNumberToHalfWidthNumber } from "@/utils/utils";

interface ChibanApi {
  convertResultToChibans: (response: ChibanSearchRequest) => SearchResultRow[];
  convertToPropertySelectionRows: () => PropertySelectionRow[];
  createChibanSearchRequest: (
    params: KaokuNumberToChibanSearchParam[]
  ) => Promise<CreateKaokuChibanSearchRequestResponse | undefined>;
  getChibanMaxRequestCount: () => number;
  getChibanSearchRequest: (
    id: number
  ) => Promise<ChibanSearchRequest | undefined>;
  // 入力側: 家屋番号リスト
  kaokuNumberRows: KaokuNumberInputRow[];
  setKaokuNumberRows: React.Dispatch<
    React.SetStateAction<KaokuNumberInputRow[]>
  >;
  setHitCountsFromResponse: (response: ChibanSearchRequest) => void;
  selectedKaokuNumberRowIds: GridSelectionModel;
  setSelectedKaokuNumberRowIds: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  chibanRows: SearchResultRow[];
  setChibanRows: React.Dispatch<React.SetStateAction<SearchResultRow[]>>;
  selectedChibanRowIds: GridSelectionModel;
  setSelectedChibanRowIds: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  openSearchModalFlag: boolean;
  setOpenSearchModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  openAddRowsModalFlag: boolean;
  setOpenAddRowsModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  pollingRequestId: number | null;
  setPollingRequestId: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * 地番検索で使用するHooks及びState群（双方向対応）
 * @param {UseChibanConfig} config
 * @returns {ChibanApi}
 */
export const useChiban = (searchData: KaokuNumberInput[]): ChibanApi => {
  const kaokuNumberList = searchData;
  const { apiClient } = useApiClient();

  /**
   * 地番検索を依頼するAPIを実行する。
   * @param {KaokuNumberToChibanSearchParam[]} requests
   * @returns {Promise<CreateKaokuChibanSearchRequestResponse | undefined>}
   */
  const createChibanSearchRequest = async (
    requests: KaokuNumberToChibanSearchParam[]
  ): Promise<CreateKaokuChibanSearchRequestResponse | undefined> => {
    try {
      const response =
        await apiClient.post<CreateKaokuChibanSearchRequestResponse>(
          "/chiban/",
          { requests }
        );

      return { ...response.data, status: response.status };
    } catch (error) {
      if (isAxiosError<CreateKaokuChibanSearchRequestResponse>(error)) {
        if (
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
          HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
        }
        // Unauthorized以外のエラーが発生した場合、ステータスコードを返す
        if (error.response?.status !== HttpStatusCode.Unauthorized) {
          return {
            status:
              error.response?.status ?? HttpStatusCode.InternalServerError, // ステータスコードが取得できない場合は500を返す
            error: error.response?.data?.error,
          };
        }
      }

      // その他のエラーの場合はundefinedを返す
      return undefined;
    }
  };

  /**
   * 地番検索リクエストの詳細を取得するAPIを実行する。
   * @param {number} id
   * @returns {Promise<ChibanSearchRequest | undefined>}
   */
  const getChibanSearchRequest = async (
    id: number
  ): Promise<ChibanSearchRequest | undefined> => {
    const response = await apiClient
      .get<ChibanSearchRequest>(`/chiban/${id}`)
      .catch((error): void => {
        if (
          isAxiosError(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
        }

        return undefined;
      });

    if (response) {
      return response.status === HttpStatusCode.Ok ? response.data : undefined;
    }
  };

  /**
   * 環境変数からVITE_KAOKU_NUMBER_MAX_REQUEST_COUNTを読み込んで数値型で返す。
   * 非数の場合はエラーを投げる。
   * @returns {number}
   */
  const getChibanMaxRequestCount = (): number => {
    const maxRequestCount = Number(
      process.env.VITE_KAOKU_NUMBER_MAX_REQUEST_COUNT
    );
    if (isNaN(maxRequestCount))
      throw new Error(
        "環境変数にVITE_KAOKU_NUMBER_MAX_REQUEST_COUNTが設定されていません。"
      );

    return maxRequestCount;
  };

  /**
   * ChibanSearchRequestをSearchResultRow[]に変換する。
   * @param {ChibanSearchRequest} response
   * @returns {SearchResultRow[]}
   */
  const convertResultToChibans = (
    response: ChibanSearchRequest
  ): SearchResultRow[] => {
    const chibanList: SearchResultRow[] = [];

    if (response.responses) {
      response.responses.results.forEach((result) => {
        result.chibanList.forEach((chiban) => {
          chibanList.push({
            id: chibanList.length,
            selected: true,
            prefectureName: result.pref,
            location: result.shozai,
            chiban,
            chibanFull: `${result.pref}${result.shozai}${chiban}`,
            kaokuNumber: result.kaokuNumber,
          });
        });
      });
    }

    return chibanList;
  };

  /**
   * SearchResultRow[]をPropertySelectionRow[]に変換する。
   * @returns {PropertySelectionRow[]}
   */
  const convertToPropertySelectionRows = (): PropertySelectionRow[] => {
    // useChiban は家屋番号→地番の検索専用のため、結果は土地（LAND）
    return chibanRows
      .filter((row) => selectedChibanRowIds.includes(row.id))
      .map((row, index) => {
        return {
          id: index,
          bookType: "LAND" as BookTypeEng,
          selected: true,
          prefName: row.prefectureName,
          location: row.location,
          chibanKaokuNumber: row.chiban,
          area: null,
          isFeedOrigin: false,
        };
      });
  };

  // ローディングのState
  const [loading, setLoading] = useState<boolean>(false);
  // 入力側: 家屋番号領域に表示するリストのState
  const [kaokuNumberRows, setKaokuNumberRows] = useState<KaokuNumberInputRow[]>(
    () =>
      kaokuNumberList.map((row, index) => ({
        id: index,
        selected: true,
        prefectureName: row.prefectureName,
        location: row.location,
        kaokuNumber: row.kaokuNumber,
      }))
  );
  // 入力側: 家屋番号領域で選択された行を保持するState
  const [selectedKaokuNumberRowIds, setSelectedKaokuNumberRowIds] =
    useState<GridSelectionModel>(() =>
      kaokuNumberList.map((_, index) => index)
    );
  // 結果側: 地番領域に表示するリストのState
  const [chibanRows, setChibanRows] = useState<SearchResultRow[]>([]);
  // 結果側: 地番領域で選択された行を保持するState
  const [selectedChibanRowIds, setSelectedChibanRowIds] =
    useState<GridSelectionModel>([]);
  // 地番領域のボタン押下時確認ダイアログ表示State
  const [openSearchModalFlag, setOpenSearchModalFlag] =
    useState<boolean>(false);
  // 家屋番号領域のボタン押下時確認ダイアログ表示State
  const [openAddRowsModalFlag, setOpenAddRowsModalFlag] =
    useState<boolean>(false);
  // ポーリングしているリクエストIDのState
  const [pollingRequestId, setPollingRequestId] = useState<number | null>(null);

  /**
   * 検索レスポンスから家屋番号ごとのヒット件数を集計して入力行に設定する
   * @param {ChibanSearchRequest} response - 地番検索のレスポンス
   */
  const setHitCountsFromResponse = (response: ChibanSearchRequest): void => {
    if (!response.responses) return;

    const hitCountMap = new Map<string, number>();
    response.responses.results.forEach((result) => {
      // 入力データ（全角）とレスポンスデータ（半角）のキーを一致させるため、
      // 両方を半角に正規化してマッチングを行う
      const key = NYFillWidthNumberToHalfWidthNumber(
        `${result.pref}${result.shozai}${result.kaokuNumber}`
      );
      hitCountMap.set(key, result.chibanList.length);
    });

    setKaokuNumberRows((prevRows) =>
      prevRows.map((row) => {
        // チェックボックスが外されている行（検索対象外）はundefinedのまま
        if (!selectedKaokuNumberRowIds.includes(row.id)) {
          return { ...row, hitCount: undefined };
        }

        // 入力行のキーも半角に正規化して、レスポンスのキーと一致させる
        const key = NYFillWidthNumberToHalfWidthNumber(
          `${row.prefectureName}${row.location}${row.kaokuNumber}`
        );
        return {
          ...row,
          hitCount: hitCountMap.get(key) ?? 0,
        };
      })
    );
  };

  return {
    convertResultToChibans,
    convertToPropertySelectionRows,
    createChibanSearchRequest,
    getChibanMaxRequestCount,
    getChibanSearchRequest,
    kaokuNumberRows,
    setKaokuNumberRows,
    setHitCountsFromResponse,
    chibanRows,
    setChibanRows,
    loading,
    setLoading,
    selectedChibanRowIds,
    setSelectedChibanRowIds,
    selectedKaokuNumberRowIds,
    setSelectedKaokuNumberRowIds,
    openSearchModalFlag,
    setOpenSearchModalFlag,
    openAddRowsModalFlag,
    setOpenAddRowsModalFlag,
    pollingRequestId,
    setPollingRequestId,
  };
};
